import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { Button } from "@sarva/ui";
import { useSession } from "@/features/auth";
import { DemoTour } from "@/features/demo-tour";
import { Brand } from "@/shared/layout/Brand";
import { embedRequested, finishEmbedded } from "@/shared/embed";
import { CommandPalette } from "@/shared/layout/CommandPalette";
import { Sidebar } from "@/shared/layout/Sidebar";
import { TopNav } from "@/shared/layout/TopNav";
import { ShortcutSheet } from "@/shared/ShortcutSheet";

const COLLAPSED_KEY = "sarva.sidebar.collapsed";

/**
 * Lazy, and for a bundle reason rather than a stylistic one: a *static* import
 * of the practices barrel here would pull `PracticesPage` and its three dialogs
 * into the entry chunk, because Rollup cannot move a module into a lazy chunk
 * once anything imports it eagerly. The router already loads that barrel
 * dynamically, so importing it the same way keeps the whole feature in one
 * split chunk and the initial route inside its budget (doc 27 §8).
 */
const PracticeSwitcher = lazy(async () => ({
  default: (await import("@/features/practices")).PracticeSwitcher,
}));

/**
 * doc 40 §8 — the application shell.
 *
 *   ┌──────────┬──────────────────────────────────────┐
 *   │ sidebar  │ topnav                       56px    │
 *   │ 240px    ├──────────────────────────────────────┤
 *   │ (48px    │ content — bg-canvas                  │
 *   │  collapsed)│  max-w 1280px, centred, 24px gutters│
 *   └──────────┴──────────────────────────────────────┘
 *
 * The sidebar is a flex sibling of the main column rather than a fixed
 * element, so collapsing it reflows the content instead of sliding over it.
 * That matters on a 13" laptop with a wide table open, which is the machine
 * this product actually runs on.
 */
export function AppLayout() {
  const { me } = useSession();
  const launch = me.launch ?? null;
  return embedRequested() || launch !== null ? (
    <EmbeddedLayout from={launch?.issuer_display_name ?? null} />
  ) : (
    <FullLayout />
  );
}

/**
 * Embedded partner mode (see `shared/embed.ts`): the page and nothing else. No rail, no top
 * navigation, no palette — the person is working inside another product and came for one
 * visit's superbill or one patient's account. "Done" hands control back to that product.
 */
function EmbeddedLayout({ from }: { from: string | null }) {
  const [stillOpen, setStillOpen] = useState(false);
  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-canvas">
      <header className="flex h-12 shrink-0 items-center justify-between gap-3 border-b border-default bg-default px-4">
        <div className="flex min-w-0 items-center gap-3">
          <Brand className="h-6" decorative />
          <span className="truncate text-xs text-secondary">
            {from === null ? "Embedded" : `Opened from ${from}`}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {stillOpen ? (
            <span role="status" className="text-xs text-secondary">
              You can close this tab now.
            </span>
          ) : null}
          <Button
            variant="secondary"
            onClick={() => {
              void finishEmbedded().then((handedBack) =>
                setStillOpen(!handedBack),
              );
            }}
          >
            Done
          </Button>
        </div>
      </header>
      <main
        id="main"
        className="min-h-0 flex-1 overflow-y-auto bg-canvas px-4 pb-8"
      >
        <div className="mx-auto flex min-h-full w-full max-w-content flex-col pt-4">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function FullLayout() {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    // Storage can be absent (a hardened profile, a test env) or throw outright
    // (Safari private mode). A rail preference is never worth taking the
    // application down for.
    try {
      return window.localStorage?.getItem?.(COLLAPSED_KEY) === "true";
    } catch {
      return false;
    }
  });
  const [searchOpen, setSearchOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage?.setItem?.(COLLAPSED_KEY, String(next));
      } catch {
        // A blocked storage API must never break the app.
      }
      return next;
    });
  }, []);

  // ⌘K / Ctrl-K opens the palette; `?` opens the shortcut sheet (doc 27 §9:
  // every high-volume workflow is completable by keyboard, with a
  // discoverable shortcut sheet). Both are ignored while the user is typing.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      const target = event.target as HTMLElement | null;
      const typing =
        target !== null &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable);

      if (event.key.toLowerCase() === "k" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setSearchOpen(true);
        return;
      }
      if (event.key === "?" && !typing && !event.metaKey && !event.ctrlKey) {
        event.preventDefault();
        setShortcutsOpen(true);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const openSearch = useCallback(() => setSearchOpen(true), []);
  const openShortcuts = useCallback(() => setShortcutsOpen(true), []);

  return (
    // (H) `h-dvh`, never `h-screen`. `100vh` is not the visible viewport: with
    // browser chrome, a zoom level, or a mobile toolbar it is *taller*, and
    // because this shell clips its overflow the bottom of `<main>` — including
    // the end of its scroll range — then sits below the visible area with no way
    // to reach it. The app reads as "scrolling is broken" while every scroll
    // container is in fact working, which is why this is easy to misdiagnose:
    // it cannot reproduce in a headless browser, where `visualViewport.height`
    // always equals `innerHeight`. `100dvh` tracks the viewport that is actually
    // visible.
    <div className="flex h-dvh overflow-hidden bg-canvas">
      <a
        href="#main"
        className={[
          "sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-toast",
          "focus:rounded-sm focus:border focus:border-strong focus:bg-default",
          "focus:px-3 focus:py-2 focus:text-sm focus:text-default",
        ].join(" ")}
      >
        Skip to content
      </a>

      <Sidebar
        collapsed={collapsed}
        onToggleCollapsed={toggleCollapsed}
        onOpenSearch={openSearch}
        /*
         * No counts are passed, and that is the honest state rather than an
         * oversight.
         *
         * This used to pass `{ queue: 42, obligations: 7 }` — two invented
         * numbers rendered as count chips beside real navigation. There is no
         * number to replace them with: `GET /work-items` and `GET /obligations`
         * both omit `meta.total_count` on purpose ("queue depth is a metric
         * with its own read model", says the work-items route's own
         * description), so the only count this client could produce is the
         * length of one page — which is not a queue depth and would be a wrong
         * number that looks right.
         *
         * `SidebarProps.counts` stays optional for the day that read model
         * lands. Until then no chip renders, which tells a reader nothing
         * rather than telling them something false.
         */
      />

      <div className="flex min-w-0 flex-1 flex-col">
        <TopNav
          onOpenSearch={openSearch}
          onOpenShortcuts={openShortcuts}
          // The tenant's display name and the active practice, from `GET /me`
          // (doc 22 §11): the practice a person is working in is the single most
          // consequential piece of context on any screen in this product.
          leading={
            // The fallback reserves the switcher's height so the bar does not
            // jump when it arrives (doc 27 §8: layout shift avoided).
            <Suspense fallback={<div className="h-9" />}>
              <PracticeSwitcher />
            </Suspense>
          }
        />
        {/* doc 40 §8: content on bg-canvas, max-width 1280px, 24px gutters.
            ── The padding lives HERE, on the scroll container ────────────────────────
            It used to be on the inner wrapper, and that produced a gap that changed from
            page to page: measured at 1440×900, the space under the last element was 16px
            on `/denials` and **0px** on the dashboard, from identical class names. The
            wrapper was `h-full`, so on a page whose content overflows it, the wrapper's
            `padding-bottom` sits at y=844 — *inside* the content — and the last panel runs
            flush against the viewport edge. On a page that fits, the same 16px shows.
            A scroll container's padding is part of its scrollable overflow region, so
            putting it here makes the gap survive scrolling and be the same everywhere.
            See docs/30-design/42_DESIGN_AUDIT.md, D24. */}
        {/* `pb-4` here, `pt-4` on the wrapper below — and the split is not arbitrary.
            Bottom padding must be on the *scroll container* so it is part of the scrollable
            overflow region and the gap survives scrolling (the whole point of D24). Top
            padding must NOT be: `sticky top-0` on a table header resolves against this
            element's padding box, so 16px of top padding left the header pinned 16px down
            with a strip of canvas above it — and rows scrolled visibly through that strip.
            Inside the wrapper the top space scrolls away with the content, which is what a
            page's leading gap should do anyway. */}
        <main
          id="main"
          className="min-h-0 flex-1 overflow-y-auto bg-canvas px-6 pb-8"
        >
          {/* `min-h-full`, not `h-full`. The page is the one vertical scroller in this
              application and every screen behaves the same way: content flows, `<main>`
              scrolls. `h-full` pinned every screen to the viewport, which made a list
              screen's table own a second scrollport — so the page itself would not move
              while 333px of rows sat hidden behind an inner scrollbar (D25).
              `min-h-full` keeps a short page's footer at the bottom without capping a
              long one. */}
          {/* `pt-6`/`pb-8`, not the 16px both ends had. D11 cut vertical padding to 16px to
              win rows back on a worklist, and that trade stopped making sense the moment
              the page began to scroll (D25) — 16px buys half a row and costs the page its
              edges. Content ending 16px from the viewport bottom reads as *clipped*, which
              is what "not scrolling till the end" described even on screens that were
              scrolling to their end. The bottom is deliberately larger than the top: a
              scrolled-to-the-end page needs to look finished, and the topnav's own border
              already separates the title from the chrome above it. */}
          <div className="mx-auto flex min-h-full w-full max-w-content flex-col pt-6">
            <Outlet />
          </div>
        </main>
      </div>

      <CommandPalette open={searchOpen} onOpenChange={setSearchOpen} />
      <ShortcutSheet open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
      {/* The presenter's guide: renders nothing unless `?tour` started it. */}
      <DemoTour />
    </div>
  );
}
